import { useLocation } from '@reach/router';
import { navigate } from 'gatsby-link';
import { parse } from 'query-string';
import React, { useEffect, useState } from 'react';
import styled, { ThemeProvider } from 'styled-components';

import FlowFooter from '@ui/atoms/FlowFooter';
import Grid from '@ui/atoms/Grid';

import CenteredGridItem from '@ui/molecules/CenteredGridItem';

import NewFlowHeader from '@ui/organisms/NewFlowHeader';
import PageContainer from '@ui/organisms/PageContainer';

import { useTracking } from '@hooks/useTracking';

import { mapKeys } from '@utils/mapKeys';

import { AuthStep, AuthStepContext, useAuth } from '@providers/AuthProvider';

import { FlowTypes } from '@folx-types/flow';

import { getThemeForFlow } from '@theme/FlowThemes';
import theme from '@theme/FolxTheme';

import PhoneNumberEnrollmentForm from './PhoneNumberEnrollmentForm';
import { UnverifiedSMSCodeInputForm, VerifiedSMSCodeInputForm } from './SMSCodeInputForm';
import SignInform from './SignInForm';
import TOTPCodeInputFormPage from './TOTPCodeInputForm';
import awsAuthErrorMessages from './awsAuthErrorMessages';

interface ISignInState {
	redirectTo?: string;
	destinationFlow?: FlowTypes;
	totalSteps?: number;
	step?: number;
	previousPage?: string;
	source?: 'zendesk';
}

const StyledGrid = styled(Grid)`
	margin-top: ${theme.spacing.lgxl};
`;

const snakeToCamel = (s: string): string => s.replace(/_[a-z]/, (s) => s[1].toUpperCase());

const getProgressStep = (
	authStep: AuthStepContext,
	step = 0,
	totalSteps = 0
): { step: number; totalSteps: number } => {
	switch (authStep.nextStep) {
		case AuthStep.needSMSMultifactorVerification:
			return {
				step: step + 1,
				totalSteps: totalSteps + 2,
			};
		case AuthStep.needSMSMultifactorSetupPhone:
			return {
				step: step + 1,
				totalSteps: totalSteps + 3,
			};
		case AuthStep.needSMSMultifactorSetupVerification:
			return {
				step: step + 2,
				totalSteps: step + 3,
			};
		default:
			return {
				step: step + 0,
				totalSteps: step + 1,
			};
	}
};

const SignIn = () => {
	const location = useLocation();
	const { loggedIn, initialized, signIn, setUpSMSMultifactor } = useAuth();
	const {
		redirectTo,
		destinationFlow,
		totalSteps: initialTotalSteps,
		step: initialStep,
		previousPage,
		source,
	} = (location.state ??
		mapKeys(snakeToCamel, parse(location.search, { parseNumbers: true }))) as ISignInState;
	const [authStep, setAuthStep] = useState<AuthStepContext>({
		nextStep: AuthStep.needPassword,
	});

	const [phoneNumber, setPhoneNumber] = useState<string>('');
	const [credentials, setCredentials] = useState<Partial<{ email: string; password: string }>>({
		email: '',
		password: '',
	});

	const { trackEvent } = useTracking('login-form-viewed');

	const redirectToHelpCenter = async () => {
		await navigate(`/app/zendesk_sso?return_to=${process.env.GATSBY_SUPPORT_LINK}`);
	};

	const resendCode = async () => {
		try {
			const newAuthStep = await signIn(credentials.email ?? '', credentials.password ?? '');
			setAuthStep({ ...authStep, user: newAuthStep?.user });
			return Promise.resolve();
		} catch (e: any) {
			if (
				e?.message === awsAuthErrorMessages.expiredSession.awsErrorMessage ||
				e?.message === awsAuthErrorMessages.invalidSession.awsErrorMessage
			) {
				setAuthStep({ nextStep: AuthStep.needPassword });
			}

			return Promise.reject(Error(awsAuthErrorMessages.errorSendingNewCode.userErrorMessage));
		}
	};

	const resendCodeEnrollment = async () => {
		try {
			const newAuthStep = await setUpSMSMultifactor(authStep?.user, phoneNumber ?? '');
			setAuthStep({ ...authStep, user: newAuthStep?.user } as AuthStepContext);
			return Promise.resolve();
		} catch (e: any) {
			if (
				e?.message === awsAuthErrorMessages.expiredSession.awsErrorMessage ||
				e?.message === awsAuthErrorMessages.invalidSession.awsErrorMessage
			) {
				setAuthStep({ nextStep: AuthStep.needPassword });
			}

			return Promise.reject(Error(awsAuthErrorMessages.errorSendingNewCode.userErrorMessage));
		}
	};

	useEffect(() => {
		if (initialized && loggedIn) {
			if (source === 'zendesk') {
				redirectToHelpCenter();
				return;
			}

			console.log('Showing the sign in page while signed in? Sending you away');
			navigate(redirectTo ?? '/app/member');
		}
	}, [initialized]);

	const onComplete = async () => {
		trackEvent('login-succeeded');

		if (source === 'zendesk') {
			redirectToHelpCenter();
			return;
		}

		await navigate(redirectTo ?? '/app/member');
	};

	const { step, totalSteps } = getProgressStep(authStep, initialStep, initialTotalSteps);

	const renderStep = (currentStep: AuthStep) => {
		switch (currentStep) {
			case AuthStep.needPassword:
				return (
					<SignInform
						setCredentials={setCredentials}
						onComplete={onComplete}
						setAuthStep={setAuthStep}
						previousPage={previousPage}
					/>
				);
			case AuthStep.needSMSMultifactorVerification:
				return (
					<VerifiedSMSCodeInputForm
						authStep={authStep}
						onComplete={onComplete}
						setAuthStep={setAuthStep}
						resendCode={resendCode}
					/>
				);

			case AuthStep.needSMSMultifactorSetupPhone:
				return (
					<PhoneNumberEnrollmentForm
						authStep={authStep}
						setAuthStep={setAuthStep}
						setPhoneNumber={setPhoneNumber}
					/>
				);
			case AuthStep.needSMSMultifactorSetupVerification:
				return (
					<UnverifiedSMSCodeInputForm
						authStep={authStep}
						onComplete={onComplete}
						setAuthStep={setAuthStep}
						phoneNumber={phoneNumber}
						resendCode={resendCodeEnrollment}
					/>
				);

			case AuthStep.needTOTPMultifactorSetup:
			case AuthStep.needTOTPMultifactorVerification:
				return (
					<TOTPCodeInputFormPage
						authStep={authStep}
						onComplete={onComplete}
						setAuthStep={setAuthStep}
					/>
				);
			default:
				return <div />;
		}
	};

	return (
		<ThemeProvider theme={getThemeForFlow(destinationFlow ?? 'onboarding')}>
			<PageContainer
				header={
					<NewFlowHeader
						flowName="Sign In"
						totalSteps={totalSteps}
						step={step}
						showSignIn={false}
					/>
				}
				footer={<FlowFooter />}>
				<StyledGrid>
					<CenteredGridItem>{renderStep(authStep.nextStep as AuthStep)}</CenteredGridItem>
				</StyledGrid>
			</PageContainer>
		</ThemeProvider>
	);
};

export default SignIn;
